/**
 * Deploy API 타입 정의
 */

export type DeploymentStatus =
  | 'deployment_succeeded'
  | 'deployment_failed'
  | 'serving_active'
  | 'serving_inactive'
  | 'serving_failed'
  | 'serving_in_progress'
  | 'deployment_in_progress';

export const DEPLOYMENT_STATUS = {
  DEPLOYMENT_SUCCEEDED: 'deployment_succeeded' as const,
  DEPLOYMENT_FAILED: 'deployment_failed' as const,
  SERVING_ACTIVE: 'serving_active' as const,
  SERVING_INACTIVE: 'serving_inactive' as const,
  SERVING_FAILED: 'serving_failed' as const,
  SERVING_IN_PROGRESS: 'serving_in_progress' as const,
  DEPLOYMENT_IN_PROGRESS: 'deployment_in_progress' as const,
} as const;

export type ModelConfig = {
  model_name: string;
  model_version: string;
  model_id: string;
  model_format?: string;
  instance_count: number;
};

export type CreateDeploymentRequest = {
  deployment_name: string;
  run_id?: string | null;
  environment: string;
  server_type: string;
  backend?: string;
  convert_to_onnx?: boolean;
  runtime_mode: string;
  replicas: number;
  models: ModelConfig[];
};

export type DeploymentResponse = {
  deployment_id: string;
  status: DeploymentStatus;
  message: string;
  details?: {
    replicas?: number;
  };
};

export type DeploymentDetailResponse = {
  deployment_id: string;
  deployment_name: string;
  model_name?: string;
  model_version?: string;
  model_format?: string | null;
  environment: string;
  status: DeploymentStatus;
  replicas: number;
  backend?: string | null;
  convert_to_onnx?: boolean | null;
  deployed_at?: string | null;
  deleted_at?: string | null;
  updated_at?: string;
  endpoints?: {
    http?: string;
    grpc?: string;
    metrics?: string;
  };
  namespace?: string;
  server_name?: string;
  runtime_mode?: string;
  cpu?: string;
  memory?: string;
  metadata?: {
    endpoints?: {
      http?: string;
      grpc?: string;
      metrics?: string;
    };
    hpa?: {
      enabled: boolean;
      min_replicas: number | null;
      max_replicas: number | null;
      target_cpu_utilization: number | null;
      target_memory_utilization: number | null;
    };
  };
};

export type UpdateDeploymentRequest = {
  replicas?: number;
  hpa_enabled?: boolean;
};

export type UpdateDeploymentResponse = {
  message: string;
  deployment_id: string;
  revision?: number;
  updated_fields?: string[];
  change_type?: string;
  details?: {
    replicas?: number;
  };
};

export type DeploymentErrorSummary = {
  error_code: string;
  category: string;
  failed_stage: string;
  message: string;
  user_action: string;
  retryable: boolean;
  occurred_at: string;
};

export type DeploymentChange = {
  deployment_id: string;
  deployment_name: string;
  status: DeploymentStatus;
  replicas: number;
  deployed_at: string | null;
  deleted_at: string | null;
  updated_at: string;
  hpa: {
    enabled: boolean;
    min_replicas: number | null;
    max_replicas: number | null;
    target_cpu_utilization: number | null;
    target_memory_utilization: number | null;
  };
  last_error_summary: DeploymentErrorSummary | null;
};

export type DeploymentChangesEvent = {
  cursor: string;
  count: number;
  changes: DeploymentChange[];
};

export type DeploymentsByRunIdsRequest = {
  run_ids: string[];
  environment?: string | null;
  include_deleted?: boolean;
};

export type DeploymentLookupByRunIdItem = {
  run_id: string;
  found: boolean;
  deployment?: {
    deployment_id: string;
    deployment_name: string;
    model_name: string;
    model_version: string;
    run_id: string | null;
    environment: string;
    namespace: string;
    server_name: string;
    server_type: string | null;
    backend: string | null;
    model_format: string | null;
    convert_to_onnx: boolean | null;
    runtime_mode: string | null;
    status: DeploymentStatus;
    replicas: number | null;
    gpu_count: number | null;
    gpu_type: string | null;
    created_at: string;
    updated_at: string | null;
    deployed_at: string | null;
    deleted_at: string | null;
  } | null;
};

export type DeploymentsByRunIdsResponse = {
  results: DeploymentLookupByRunIdItem[];
};
